import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { useHlsInstance } from '@/hooks/useHlsInstance'
import { HlsErrorOverlay } from '@/organisms/HlsPlayer/HlsErrorOverlay'
import { HlsPlayerUI } from '@/organisms/HlsPlayer/HlsPlayerUI'
import type { Asset, PlayerOverlay, UpNextConfig } from '../BitmovinPlayer/types'
import { HlsPlayerController } from './HlsPlayerController'

interface HlsPlayerProps {
  asset: Asset
  autoplay?: boolean
  aspectClassName?: string
  overlays?: PlayerOverlay[]
  isMuted?: boolean
  introStartTime?: number | null
  introEndTime?: number | null
  episodeGuid?: string
  upNext?: UpNextConfig
  showContentShareButton?: boolean
  onShare?: () => void
  showParentalControlsButton?: boolean
  parentalControlsEnabled?: boolean
  parentalControlsApplied?: number
  parentalControlsTotal?: number
  onOpenParentalControls?: () => void
}

/**
 * HLS.js-based video player component.
 *
 * Renders an HTML5 video element with HLS.js for adaptive streaming,
 * overlaid with custom playback controls (HlsPlayerUI).
 */
export const HlsPlayer: React.FC<HlsPlayerProps> = ({
  asset,
  autoplay,
  aspectClassName = 'w-full h-full',
  overlays,
  isMuted,
  introStartTime,
  introEndTime,
  episodeGuid,
  upNext,
  showContentShareButton,
  onShare,
  showParentalControlsButton,
  parentalControlsEnabled,
  parentalControlsApplied,
  parentalControlsTotal,
  onOpenParentalControls,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const controllerRef = useRef<HlsPlayerController | null>(null)
  const [controllerReady, setControllerReady] = useState(false)
  const [fatalError, setFatalError] = useState<string | null>(null)
  const retryCountRef = useRef(0)
  const loadedUrlRef = useRef<string | null>(null)

  const handleFatalError = (message: string) => {
    setFatalError(message)
  }

  const { hlsRef, isReady } = useHlsInstance({
    videoRef,
    url: asset.sourceConfig.hls,
    muted: isMuted,
    onFatalError: handleFatalError,
    // Phase 6: DRM config will be passed here via hlsConfig
  })

  // Initialize controller when HLS.js source is ready.
  // At that point hlsRef.current is guaranteed to be set.
  useEffect(() => {
    if (!isReady) return

    const video = videoRef.current
    const hls = hlsRef.current
    if (!video || !hls) return

    const controller = new HlsPlayerController(video, hls, asset, autoplay)
    controllerRef.current = controller
    loadedUrlRef.current = asset.sourceConfig.hls
    setControllerReady(true)
    // TODO: Wire AngelPlayerContext setController once we resolve the re-render/unmount issue.
    // Calling setController here triggers a parent re-render that unmounts HlsPlayer,
    // destroying the HLS instance. For now, the UI accesses the controller via controllerRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once when isReady becomes true
  }, [isReady])

  // Handle asset changes (e.g., switching episodes).
  // Only reload if the streaming URL actually changed — the asset object reference
  // changes frequently (e.g., when nextContent arrives or useEpisodeAsset recomputes)
  // but the URL stays the same. Reloading on every reference change destroys buffered
  // data and makes the player unresponsive.
  useEffect(() => {
    const controller = controllerRef.current
    if (!controller) return
    const newUrl = asset.sourceConfig.hls
    if (newUrl === loadedUrlRef.current) return
    loadedUrlRef.current = newUrl

    controller.load({
      sourceConfig: asset.sourceConfig,
      name: asset.name,
      framesPerSecond: asset.frameRate,
      adjustmentFactor: asset.adjustmentFactor,
      framesDroppedAtFullMinute: asset.framesDroppedEachMinute,
      frameDuration: 1 / asset.frameRate,
      offsetToMidFrame: 0,
      position: asset.position,
    })
  }, [asset])

  const handleRetry = () => {
    retryCountRef.current += 1
    setFatalError(null)
    const hls = hlsRef.current
    if (hls) {
      hls.loadSource(asset.sourceConfig.hls)
    }
  }

  return (
    <div ref={containerRef} id="hlsjs-player" className={classNames([aspectClassName, 'relative bg-black'])}>
      <video ref={videoRef} className="h-full w-full object-contain" playsInline />
      {controllerReady && !fatalError && (
        <HlsPlayerUI
          videoRef={videoRef}
          controller={controllerRef.current}
          containerRef={containerRef}
          asset={asset}
          introStartTime={introStartTime}
          introEndTime={introEndTime}
          episodeGuid={episodeGuid}
          upNext={upNext}
          showContentShareButton={showContentShareButton}
          onShare={onShare}
          showParentalControlsButton={showParentalControlsButton}
          parentalControlsEnabled={parentalControlsEnabled}
          parentalControlsApplied={parentalControlsApplied}
          parentalControlsTotal={parentalControlsTotal}
          onOpenParentalControls={onOpenParentalControls}
        />
      )}
      {fatalError && <HlsErrorOverlay message={fatalError} retryCount={retryCountRef.current} onRetry={handleRetry} />}
      {overlays?.map(({ key, Component }) => (
        <Component key={key} />
      ))}
    </div>
  )
}
